import { Link } from 'react-router-dom';
import type { ImportantDateWithOccurrence } from '../../api/types';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent } from '../../components/ui/card';
import {
  formatDaysLeft,
  formatImportantDateMetric,
  formatImportantDateTypeLabel,
  formatOccurrenceDate,
  formatOriginalDateLabel,
  formatPersonMeta,
} from '../../utils/format';

type DateCardProps = {
  date: ImportantDateWithOccurrence;
  showPerson?: boolean;
};

export const DateCard = ({ date, showPerson = true }: DateCardProps) => {
  const dday = formatDaysLeft(date.occurrence.daysLeft, date.occurrence.dDayLabel);
  return (
    <Link to={`/important-dates/${date.id}/edit`} className="block">
      <Card className="min-h-[112px] transition-colors hover:border-primary/60">
        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="text-xs font-semibold uppercase tracking-[0.08em] text-primary">{formatImportantDateTypeLabel(date.type)}</p>
              <h3 className="mt-1 line-clamp-2 text-[18px] font-semibold leading-6 tracking-[-0.01em] text-foreground">{date.title}</h3>
              {showPerson && date.person ? (
                <p className="mt-1 text-sm text-muted-foreground">
                  {date.person.name} · {formatPersonMeta(date.person)}
                </p>
              ) : null}
            </div>
            <Badge className={date.occurrence.isToday ? 'bg-primary text-primary-foreground' : 'bg-primary/10 text-primary'}>
              {dday}
            </Badge>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-2 text-sm">
            <Meta label="다음">{formatOccurrenceDate(date.occurrence.nextOccurrenceDate)}</Meta>
            <Meta label="원본">{formatOriginalDateLabel(date)}</Meta>
            <Meta label="계산">{formatImportantDateMetric(date)}</Meta>
            <Meta label="알림">
              {date.reminderEnabled && date.occurrence.reminderDate ? `${date.reminderOffsetDays}일 전` : '꺼짐'}
            </Meta>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
};

const Meta = ({ label, children }: { label: string; children: string }) => (
  <div className="border-t border-border px-1 py-2">
    <p className="text-xs font-medium text-muted-foreground">{label}</p>
    <p className="mt-1 truncate text-sm font-medium text-foreground">{children}</p>
  </div>
);
